import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Button from './Button';
import Modal from './Modal';
import { useTranslation } from 'react-i18next';
import { IRequest } from '../common/interfaces/request.interface';
import Select, { SelectItem } from './Select';
import { useRequestNextMonthQuery } from '../services/requests/requests.service';
import { useErrorToast } from '../hooks/useToast';

interface RequestYearMonthPickerModalProps {
  title: string;
  onClose: () => void;
  onSubmit: (payload: { year: SelectItem<string> | undefined, month: SelectItem<string> | undefined }) => void;
  buttonLabel: string;
  request: IRequest | null
}


const RequesYearMonthPickertModal = ({ title, onClose, onSubmit, buttonLabel, request }: RequestYearMonthPickerModalProps) => {
  const { t } = useTranslation('request_navigation_modal');
  const {
    handleSubmit,
    control,
    watch,
    reset,
    setValue,
    formState: { isDirty }
  } = useForm<{ year: SelectItem<string> | undefined, month: SelectItem<string> | undefined }>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const {
    data: requestNextMonth,
    error: requestNextMonthError,
  } = useRequestNextMonthQuery(request?.id);

  useEffect(() => {
    if (requestNextMonthError) {
      useErrorToast('Eroare la preluarea datelor.');
    }
  }, [requestNextMonthError]);

  useEffect(() => {
    if (requestNextMonth) {
      reset(
        {
          year: { key: `${requestNextMonth?.next_year}`, value: `${requestNextMonth?.next_year}` },
          month: { key: `${requestNextMonth?.next_month}`, value: `${requestNextMonth?.next_month}` }
        }, { keepDirty: false })
    }
  }, [requestNextMonth])

  const year = watch('year');
  const month = watch('month');

  const years = useMemo(() => {
    if (!request) {
      return [];
    }
    const startYear = new Date(request?.start_date).getFullYear();
    const endYear = new Date(request?.end_date).getFullYear();
    const yearList: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
      yearList.push(year);
    }
    return yearList?.map(((year: number) => ({ key: year.toString(), value: year.toString() })));
  }, [request])

  const months = useMemo(() => {
    if (!request || !year) {
      return [];
    }
    const yearVal = +year.value;
    const startYear = new Date(request.start_date).getFullYear();
    const endYear = new Date(request.end_date).getFullYear();
    const startMonth = yearVal === startYear ? new Date(request.start_date).getMonth() : 0;
    const endMonth = yearVal === endYear ? new Date(request.end_date).getMonth() : 11;
    const monthList: number[] = [];
    for (let month = startMonth; month <= endMonth; month++) {
      monthList.push(month + 1);
    }
    return monthList.map(((month: number) => ({ key: month.toString(), value: month.toString() })));
  }, [year])

  useEffect(() => {
    if (year && isDirty) {
      setValue('month', undefined)
    }
  }, [year, isDirty])

  return (
    <Modal title={title} onClose={onClose}>
      <div className="flex flex-col px-12 w-full min-w-xl gap-[60px]">
        <form className="gap-4" onSubmit={handleSubmit(onSubmit)}>
          <Controller
            key="year"
            name="year"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  options={years}
                  selected={value}
                  label={t('form.year.label')}
                  onChange={onChange}
                />
              );
            }}
          />
          <Controller
            key="month"
            name="month"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <Select
                  options={months}
                  selected={value}
                  label={t('form.month.label')}
                  onChange={onChange}
                />
              );
            }}
          />
        </form>
        <div className="flex flex-row-reverse">
          <Button
            id="project_form__submit-button"
            label={buttonLabel}
            className="btn-primary w-full disabled:bg-gray-500"
            onClick={handleSubmit(onSubmit)}
            disabled={!year || !month}
          />
        </div>
      </div>
    </Modal>
  );
};

export default RequesYearMonthPickertModal;
